import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  InstallTenantProductDocument,
  type InstallTenantProductMutation,
  type InstallTenantProductMutationVariables,
} from "~/features/admin/tenants/gqls";
import { translateError } from "~/lib/errors";

/** One (tenant, product) install unit of the bulk run. */
export interface BulkInstallTarget {
  tenantId: string;
  tenantName: string;
  productId: string;
  productName: string;
}

interface BulkInstallFailure extends BulkInstallTarget {
  message: string;
}

interface BulkInstallProgress {
  done: number;
  total: number;
}

export interface BulkInstallResult {
  succeeded: number;
  failures: BulkInstallFailure[];
}

/**
 * Runs cnutInstallTenantProduct sequentially over the given (tenant, product)
 * pairs. Sequential on purpose: the bulk operation is a one-off control-plane
 * action and per-row mutations keep the server-side authorization / audit path
 * identical to a manual install (no dedicated bulk API).
 */
export function useTenantBulkInstallProducts() {
  const { t } = useTranslation();
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<BulkInstallProgress | null>(null);
  const [installProduct] = useMutation<InstallTenantProductMutation, InstallTenantProductMutationVariables>(
    InstallTenantProductDocument,
  );

  const run = useCallback(
    async (targets: BulkInstallTarget[]): Promise<BulkInstallResult> => {
      setRunning(true);
      setProgress({ done: 0, total: targets.length });
      const failures: BulkInstallFailure[] = [];
      let succeeded = 0;
      try {
        for (const [index, target] of targets.entries()) {
          try {
            const res = await installProduct({
              variables: { input: { id: target.tenantId, productId: target.productId } },
            });
            const out = res.data?.cnutInstallTenantProduct;
            if (out?.__typename === "TenantInstallProductOutput") {
              succeeded += 1;
            } else if (out?.__typename === "ValidationError") {
              failures.push({ ...target, message: out.fields.map((f) => f.message).join(", ") });
            } else {
              failures.push({ ...target, message: t("error.unknown") });
            }
          } catch (e) {
            failures.push({ ...target, message: translateError(t, e) });
          }
          setProgress({ done: index + 1, total: targets.length });
        }
      } finally {
        setRunning(false);
      }
      return { succeeded, failures };
    },
    [installProduct, t],
  );

  return { run, running, progress };
}
